import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';

import { Customer360Service } from './services/customer-360.service';
import { VisitPhotosService } from './services/visit-photos.service';
import { TimelineEntry, VisitPhoto } from './models/customer-360.types';

/**
 * Customer photo gallery.
 *
 * Independent tab for the Customer 360 split view. Loads:
 *  - `visit-photos/by-customer/:userId` → flat list of all photos
 *  - `reports/customers/:userId/timeline` → recent visits, used as the
 *    dropdown source for the uploader (photos must attach to a
 *    specific booking per the CHECK constraint on `visit_photo`).
 *
 * Both requests fire in parallel on tab open via `forkJoin`, so the
 * user sees one combined spinner instead of two overlapping ones.
 *
 * UI modernization vs. the original inline gallery:
 *  - Uploader card with dashed drop-zone styling and explicit visit
 *    picker; file input is wrapped so the whole card is clickable.
 *  - Photo tiles use `nz-image`'s lightbox group (click → full preview
 *    with prev/next), with hover-revealed delete button.
 *  - Rows are grouped by visit date using a `Map` keyed on bookingId,
 *    turning a flat grid into a chronological story — matches how
 *    hair/makeup work is actually discussed ("the 6 April booking").
 */
@Component({
  selector: 'beautyapp-customer-360-photos',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    NzSpinModule,
    NzEmptyModule,
    NzButtonModule,
    NzIconModule,
    NzSelectModule,
    NzImageModule,
    NzPopconfirmModule,
  ],
  template: `
    <nz-spin [nzSpinning]="loading()" nzSize="large">
      <section class="photos">
        <header class="head">
          <div>
            <h3>ფოტოები</h3>
            <span class="sub">ვიზიტების ფოტოები — დარუფავა, მაკიაჟი, თმა და ა.შ.</span>
          </div>
          <div class="count" *ngIf="photos().length">
            {{ photos().length }} ფოტო
          </div>
        </header>

        <!-- Uploader -->
        <div class="uploader" [class.disabled]="!selectedBookingId()">
          <div class="up-left">
            <div class="up-icon"><span nz-icon nzType="cloud-upload"></span></div>
            <div class="up-text">
              <div class="up-title">ახალი ფოტოს ატვირთვა</div>
              <div class="up-hint">
                აირჩიე ვიზიტი და შემდეგ დააჭირე „ფაილების არჩევა"-ს.
                შეგიძლია ერთდროულად რამდენიმე ფოტოს ატვირთვა.
              </div>
            </div>
          </div>
          <div class="up-right">
            <nz-select
              class="visit-select"
              [ngModel]="selectedBookingId()"
              (ngModelChange)="selectedBookingId.set($event)"
              nzPlaceHolder="ვიზიტის არჩევა"
              nzShowSearch
              [nzNotFoundContent]="'ვიზიტები არ მოიძებნა'"
            >
              <nz-option
                *ngFor="let v of visits()"
                [nzValue]="v.bookingId"
                [nzLabel]="(v.date || '—') + '  •  ' + (v.employeeName || '—')"
              ></nz-option>
            </nz-select>
            <button
              nz-button
              nzType="primary"
              (click)="fileInput.click()"
              [nzLoading]="uploading()"
              [disabled]="!selectedBookingId()"
            >
              <span nz-icon nzType="upload"></span>
              ფაილების არჩევა
            </button>
            <input
              #fileInput
              type="file"
              accept="image/*"
              multiple
              hidden
              (change)="onFiles($event)"
            />
          </div>
        </div>

        <!-- Grid grouped by booking date -->
        <div class="groups" *ngIf="photos().length; else emptyTpl">
          <div class="group" *ngFor="let g of groups()">
            <div class="group-head">
              <span nz-icon nzType="calendar"></span>
              <span class="group-date">{{ g.label }}</span>
              <span class="group-count">({{ g.items.length }})</span>
            </div>
            <div class="grid">
              <div class="tile" *ngFor="let p of g.items">
                <img
                  nz-image
                  [nzSrc]="p.url"
                  [alt]="p.caption || ''"
                  class="thumb"
                />
                <button
                  class="tile-del"
                  nz-button
                  nzShape="circle"
                  nzSize="small"
                  nzDanger
                  nz-popconfirm
                  nzPopconfirmTitle="წავშალო ეს ფოტო?"
                  (nzOnConfirm)="remove(p)"
                >
                  <span nz-icon nzType="delete"></span>
                </button>
                <div class="tile-caption" *ngIf="p.caption">{{ p.caption }}</div>
              </div>
            </div>
          </div>
        </div>
        <ng-template #emptyTpl>
          <div class="empty" *ngIf="!loading()">
            <nz-empty nzNotFoundImage="simple"></nz-empty>
            <div class="empty-hint">ფოტოები ჯერ არ არის — აირჩიე ვიზიტი და ატვირთე პირველი ფოტო.</div>
          </div>
        </ng-template>
      </section>
    </nz-spin>
  `,
  styles: [`
    :host { display: block; }
    .photos { display: flex; flex-direction: column; gap: 16px; padding: 4px 4px 24px; }

    .head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
    .head h3 { margin: 0; font-size: 16px; font-weight: 650; color: #101828; }
    .head .sub { color: #98a2b3; font-size: 12px; }
    .count {
      padding: 4px 10px;
      background: #eef2ff;
      color: #4338ca;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 600;
    }

    .uploader {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 16px 18px;
      background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
      border: 1px dashed #cbd5e1;
      border-radius: 14px;
      transition: border-color 0.15s ease;
    }
    .uploader:hover { border-color: #6366f1; }
    .uploader.disabled { opacity: 0.9; }
    .up-left { display: flex; align-items: center; gap: 14px; }
    .up-icon {
      width: 48px; height: 48px;
      display: grid; place-items: center;
      border-radius: 12px;
      background: #eef2ff; color: #4f46e5;
      font-size: 22px;
    }
    .up-title { font-weight: 600; color: #101828; }
    .up-hint { color: #667085; font-size: 12px; margin-top: 2px; max-width: 420px; }
    .up-right { display: flex; align-items: center; gap: 10px; }
    .visit-select { min-width: 260px; }

    .groups { display: flex; flex-direction: column; gap: 18px; }
    .group-head {
      display: flex; align-items: center; gap: 8px;
      margin-bottom: 8px;
      color: #344054;
      font-weight: 600;
      font-size: 13px;
    }
    .group-head span[nz-icon] { color: #6366f1; }
    .group-count { color: #98a2b3; font-weight: 400; }

    .grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      gap: 12px;
    }
    .tile {
      position: relative;
      border-radius: 12px;
      overflow: hidden;
      background: #f4f6f8;
      border: 1px solid #eef0f3;
      transition: transform 0.15s ease, box-shadow 0.15s ease;
    }
    .tile:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(16,24,40,0.1);
    }
    .thumb {
      display: block;
      width: 100%;
      height: 150px;
      object-fit: cover;
      cursor: zoom-in;
    }
    .tile-del {
      position: absolute;
      top: 8px; right: 8px;
      opacity: 0;
      transition: opacity 0.15s ease;
    }
    .tile:hover .tile-del { opacity: 1; }
    .tile-caption {
      padding: 6px 10px;
      font-size: 12px;
      color: #475467;
      background: #fff;
      border-top: 1px solid #eef0f3;
    }

    .empty { display: flex; flex-direction: column; align-items: center; padding: 36px 16px; }
    .empty-hint { color: #667085; font-size: 13px; margin-top: 8px; text-align: center; }

    @media (max-width: 720px) {
      .uploader { flex-direction: column; align-items: stretch; }
      .up-right { flex-direction: column; align-items: stretch; }
      .visit-select { min-width: 0; width: 100%; }
    }
  `],
})
export class Customer360PhotosComponent {
  userId = input.required<string>();

  private analytics = inject(Customer360Service);
  private api = inject(VisitPhotosService);
  private msg = inject(NzMessageService);

  loading = signal(true);
  uploading = signal(false);
  photos = signal<VisitPhoto[]>([]);
  visits = signal<TimelineEntry[]>([]);
  selectedBookingId = signal<string | null>(null);

  /**
   * Photos grouped by bookingId → visit date (for display). Kept as a
   * computed wrapper over the flat `photos()` signal so that deletes
   * and uploads trigger the regrouping automatically.
   */
  groups = signal<{ key: string; label: string; items: VisitPhoto[] }[]>([]);

  constructor() {
    effect(() => {
      const id = this.userId();
      if (id) this.load(id);
    });
    // Regroup whenever photos or visits change.
    effect(() => {
      const ph = this.photos();
      const vs = this.visits();
      this.groups.set(this.groupPhotos(ph, vs));
    });
  }

  private load(id: string): void {
    this.loading.set(true);
    forkJoin({
      photos: this.api.listByCustomer(id, { limit: 500, offset: 0 }),
      timeline: this.analytics.getTimeline(id, { limit: 50 }),
    }).subscribe({
      next: ({ photos, timeline }) => {
        this.photos.set(photos ?? []);
        this.visits.set(timeline ?? []);
        this.loading.set(false);
      },
      error: () => {
        this.photos.set([]);
        this.visits.set([]);
        this.loading.set(false);
        this.msg.error('ფოტოების ჩატვირთვა ვერ მოხერხდა');
      },
    });
  }

  /**
   * Groups photos by bookingId and labels each bucket with the matching
   * visit date from the timeline (falling back to "ვიზიტის გარეშე" for
   * photos that somehow lack a booking link).
   */
  private groupPhotos(
    photos: VisitPhoto[],
    visits: TimelineEntry[],
  ): { key: string; label: string; items: VisitPhoto[] }[] {
    const byBooking = new Map<string, VisitPhoto[]>();
    for (const p of photos) {
      const key = p.bookingId ?? 'orphan';
      const arr = byBooking.get(key);
      if (arr) arr.push(p);
      else byBooking.set(key, [p]);
    }
    const visitByBookingId = new Map(visits.map((v) => [v.bookingId, v] as const));
    const groups = Array.from(byBooking.entries()).map(([key, items]) => {
      const v = visitByBookingId.get(key);
      const label = key === 'orphan'
        ? 'ვიზიტის გარეშე'
        : (v ? `${v.date}  •  ${v.employeeName || '—'}` : key.slice(0, 8));
      return { key, label, items };
    });
    // Newest first — sorts by the visit date when available.
    groups.sort((a, b) => b.label.localeCompare(a.label));
    return groups;
  }

  onFiles(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (!files.length) return;
    const bookingId = this.selectedBookingId();
    if (!bookingId) {
      this.msg.warning('აირჩიე ვიზიტი');
      input.value = '';
      return;
    }
    this.uploading.set(true);
    let pending = files.length;
    for (const file of files) {
      this.api.upload(file, { bookingId }).subscribe({
        next: (p) => {
          this.photos.set([p, ...this.photos()]);
          if (--pending === 0) {
            this.uploading.set(false);
            this.msg.success('ატვირთულია');
          }
        },
        error: () => {
          if (--pending === 0) this.uploading.set(false);
          this.msg.error('ატვირთვა ვერ მოხერხდა');
        },
      });
    }
    input.value = '';
  }

  remove(photo: VisitPhoto): void {
    this.api.remove(photo.id).subscribe({
      next: () => {
        this.photos.set(this.photos().filter((p) => p.id !== photo.id));
        this.msg.success('წაშლილია');
      },
      error: () => this.msg.error('წაშლა ვერ მოხერხდა'),
    });
  }
}
